import { Field, Label, Switch } from '@headlessui/react'
import { FormEventHandler, useEffect, useRef, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import styled from 'styled-components'

import ErrorComponent from '@/components/ErrorComponent'
import Page from '@/components/Page'
import useFeatures from '@/components/useFeatures'
import { useAppStore } from '@/store/hooks'
import cn from '@/utilities/cn'

const StyledPage = styled(Page)`
  .layout:has(&) {
    display: flex;
  }

  &.page-wrapper {
    height: auto;
    display: flex;
    flex-basis: 100%;

    > .flex-center {
      flex: 1;
      flex-direction: row;
    }
  }

  &.content {
    padding-top: 0;
    max-width: initial;
    flex: 1;
  }

  &:has(#workgrid-stylable-wrap) {
    .toolbar-offset {
      @media (min-width: 775px) {
        left: 45px;
      }
      @media (min-width: 992px) {
        left: 90px;
      }
    }
  }

  &:has([data-workgrid-selector='@workgrid-widgets/client-toolbar']) {
    .toolbar-offset {
      left: 60px;
    }
  }

  /* Hide devtools on this page */
  html:has(&) .ReactQueryDevtools {
    display: none;
  }
`

function SpacePlaygroundPage() {
  const features = useFeatures()
  const [demoMode, setDemoMode] = useState(false)

  useEffect(() => {
    if (!demoMode) return

    const wrapper = document.querySelector('#nueva-wrapper') as HTMLDivElement | null
    if (!wrapper) return

    const position = wrapper.style.position
    const zIndex = wrapper.style.zIndex

    wrapper.style.position = 'absolute'
    wrapper.style.zIndex = '10'

    return () => {
      wrapper.style.position = position
      wrapper.style.zIndex = zIndex
    }
  }, [demoMode])

  return (
    <StyledPage className={demoMode ? `absolute inset-0 z-10` : ''}>
      {/* @ts-expect-error */}
      <ErrorBoundary
        fallbackRender={props => (
          <div className="mx-auto max-w-2xl">
            {/* eslint-disable-next-line react/prop-types */}
            <ErrorComponent errors={props.error ? [props.error] : []} />
          </div>
        )}
      >
        <div className="relative flex-1">
          <Backdrop className="toolbar-offset" />

          <Bootstrap />

          {features['console-space-playground-demo-mode'] ? (
            <div className="toolbar-offset absolute bottom-0 left-0">
              <Field as="div" className="flex items-center space-x-3 px-3 py-2">
                <Switch
                  checked={demoMode}
                  onChange={setDemoMode}
                  className={cn(
                    demoMode ? 'bg-blue-600/80' : 'bg-blue-600/20',
                    'relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent shadow transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-600/80 focus:ring-offset-1'
                  )}
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      demoMode ? 'translate-x-4' : 'translate-x-0',
                      'pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out'
                    )}
                  />
                </Switch>
                <Label as="span" className="text-sm">
                  <span className="font-medium text-gray-700" style={{ textShadow: '0 1px 0 rgba(255,255,255,0.2)' }}>
                    Demo
                  </span>
                </Label>
              </Field>
            </div>
          ) : null}
        </div>
      </ErrorBoundary>
    </StyledPage>
  )
}

export default SpacePlaygroundPage

const backdropCache = { value: undefined as string | undefined }

function Backdrop(props: { className?: string }) {
  const [backdrop, setBackdrop] = useState(backdropCache.value)

  useEffect(() => {
    backdropCache.value = backdrop
  }, [backdrop])

  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault()

    const data = new FormData(event.currentTarget)
    setBackdrop(String(data.get('url')))
  }

  if (backdrop) {
    return (
      <div
        className={cn('absolute inset-0 bg-cover bg-left-top bg-no-repeat', props.className)}
        style={{ backgroundImage: `url(${backdrop})` }}
      />
    )
  }

  function chooseFile() {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.click()

    input.addEventListener('change', () => {
      if (!input.files?.length) return
      setBackdrop(URL.createObjectURL(input.files[0]))
    })
  }

  return (
    <div className={cn('absolute inset-0 py-16 sm:py-24', props.className)}>
      <div className="mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="relative isolate overflow-hidden bg-gray-900 px-6 py-24 shadow-2xl sm:rounded-3xl sm:px-24 xl:py-32">
          <h2 className="mx-auto max-w-2xl text-center text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Custom Background Image
          </h2>
          <form className="mx-auto mt-10 flex max-w-2xl justify-center gap-x-4" onSubmit={handleSubmit} noValidate>
            <div className="flex min-w-0 flex-auto gap-4 rounded-md bg-white/5 px-3.5 py-2 shadow-sm ring-1 ring-inset ring-white/10 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-white">
              <label htmlFor="url" className="sr-only">
                Background image url
              </label>
              <input
                id="url"
                name="url"
                type="url"
                autoComplete="url"
                required
                className="flex-1 border-0 bg-transparent p-0 text-white focus:ring-0 sm:text-sm sm:leading-6"
                placeholder="Enter image url"
              />

              <button
                className="flex items-center justify-center rounded-md px-1 text-sm text-white/80 underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white"
                onClick={() => chooseFile()}
              >
                Choose file
              </button>

              <button
                type="button"
                className="flex items-center justify-center rounded-md px-1 text-sm text-white/80 underline focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-2 focus-visible:outline-white"
                onClick={() => setBackdrop('https://workgrid.github.io/sandbox/example.png')}
              >
                Use example
              </button>
            </div>
            <button
              type="submit"
              className="flex-none rounded-md bg-white py-2.5 px-3.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              onClick={event => {
                if (!event.currentTarget.form?.reportValidity()) {
                  event.preventDefault()
                }
              }}
            >
              Set background
            </button>
          </form>
          <svg
            viewBox="0 0 1024 1024"
            className="absolute top-1/2 left-1/2 -z-10 h-[64rem] w-[64rem] -translate-x-1/2"
            aria-hidden="true"
          >
            <circle cx={512} cy={512} r={512} fill="url(#759c1415-0410-454c-8f7c-9a820de03641)" fillOpacity="0.7" />
            <defs>
              <radialGradient
                id="759c1415-0410-454c-8f7c-9a820de03641"
                cx={0}
                cy={0}
                r={1}
                gradientUnits="userSpaceOnUse"
                gradientTransform="translate(512 512) rotate(90) scale(512)"
              >
                <stop stopColor="#7775D6" />
                <stop offset={1} stopColor="#E935C1" stopOpacity={0} />
              </radialGradient>
            </defs>
          </svg>
        </div>
      </div>
    </div>
  )
}

const BootstrapWrapper = styled('div')`
  /* Legacy Toolbar */
  &:has(#workgrid-stylable-wrap) {
    #workgrid-stylable-wrap {
      position: absolute;
      height: 100%;
      z-index: 1;
    }
  }

  /* Button */
  [data-workgrid-selector='@workgrid-widgets/client-button'] {
    position: absolute !important;
    z-index: 10;
  }

  /* Toolbar */
  [data-workgrid-selector='@workgrid-widgets/client-toolbar'] {
    position: absolute !important;
    z-index: 1;
  }
`

function Bootstrap() {
  const storeRef = useRef(useAppStore())
  storeRef.current = useAppStore()

  const targetRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!storeRef.current) return

    const store = storeRef.current

    const apiHost = process.env.REACT_APP_USE_DEV_ENV
      ? 'https://workgridsoftware.dev.workgrid.com'
      : window.location.origin

    !(async () => {
      try {
        const config = await fetch(`${apiHost}/v1/admin/client-config?key=workgrid-console`, {
          headers: { Authorization: `Bearer ${store.getState().authentication.accessToken!}` }
        })
          .then(res => res.json())
          .then(data => data.data)

        const tag = config.widget.endsWith('-alpha') ? 'alpha' : 'latest'

        const hostname = /\.((dev|test)\.workgrid\.com)$/.exec(location.hostname)?.[1] ?? 'workgrid.com'

        const [bootstrap, spaceId] = await Promise.all([
          import(/* @vite-ignore */ `https://cdn.${hostname}/@workgrid-widgets/bootstrap@${tag}`).then(
            mod => mod.default
          ),

          // Wait for spaceId to be populated (avoid Nueva loading before console is ready)
          store.getState().context.spaceId ??
            new Promise(resolve => {
              const unsubscribe = store.subscribe(() => {
                const { spaceId } = store.getState().context
                if (spaceId) {
                  unsubscribe()
                  resolve(spaceId)
                }
              })
            })
        ])

        // Exit early if the target was unmounted
        if (!targetRef.current) return

        bootstrap({
          target: targetRef.current,
          getKey: () => 'workgrid-console',
          getToken: () => storeRef.current.getState().authentication.accessToken,
          getApiHost: () => apiHost,
          // Disable caching
          cacheOptions: {
            storage: {
              setItem: () => {},
              getItem: () => null,
              removeItem: () => {}
            }
          },
          // Reuse the previously fetched config, set space to match console
          networkOptions: {
            fetch(...args: Parameters<typeof fetch>) {
              const request = new Request(...args)

              if (request.url === `${apiHost}/v1/admin/client-config?key=workgrid-console`) {
                return new Response(JSON.stringify({ status: 'success', data: { ...config, space: spaceId } }))
              }

              return fetch(request)
            }
          }
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error)
      }
    })()

    return () => {
      // Make sure we cleanup global styles added by toolbar
      const style = document.documentElement.getAttribute('style') ?? ''
      document.documentElement.setAttribute('style', style.replace(' overflow: hidden;', ''))
    }
  }, [])

  // Update the key on hot-reload to ensure the element is re-created
  const [key, setKey] = useState(1)
  useEffect(() => setKey(key => key + 1), [])

  return <BootstrapWrapper key={key} ref={targetRef} />
}
